export type FareState =
	| "provider_unavailable"
	| "sample_insufficient"
	| "date_diversity_insufficient"
	| "current"
	| "stale";

export type FareObservation = {
	amountKrw: number;
	departureDate: string;
	observedAt: string;
	expiresAt?: string;
};

export type FareSummary = {
	state: FareState;
	medianKrw: number | null;
	q1Krw: number | null;
	q3Krw: number | null;
	n: number;
	distinctDepartureDates: number;
};

const percentile = (sorted: number[], p: number) => {
	const index = (sorted.length - 1) * p;
	const lower = Math.floor(index);
	const upper = Math.ceil(index);
	if (lower === upper) return sorted[lower] ?? 0;
	const low = sorted[lower] ?? 0;
	const high = sorted[upper] ?? low;
	return Math.round(low + (high - low) * (index - lower));
};

export function summarizeFare(
	observations: FareObservation[],
	nowIso: string,
): FareSummary {
	if (observations.length === 0) {
		return {
			state: "provider_unavailable",
			medianKrw: null,
			q1Krw: null,
			q3Krw: null,
			n: 0,
			distinctDepartureDates: 0,
		};
	}
	const valid = observations.filter(
		(item) => !item.expiresAt || item.expiresAt > nowIso,
	);
	const dates = new Set(valid.map((item) => item.departureDate)).size;
	if (valid.length < 30)
		return {
			state: "sample_insufficient",
			medianKrw: null,
			q1Krw: null,
			q3Krw: null,
			n: valid.length,
			distinctDepartureDates: dates,
		};
	if (dates < 12)
		return {
			state: "date_diversity_insufficient",
			medianKrw: null,
			q1Krw: null,
			q3Krw: null,
			n: valid.length,
			distinctDepartureDates: dates,
		};
	const values = valid.map((item) => item.amountKrw).sort((a, b) => a - b);
	return {
		state: "current",
		medianKrw: percentile(values, 0.5),
		q1Krw: percentile(values, 0.25),
		q3Krw: percentile(values, 0.75),
		n: valid.length,
		distinctDepartureDates: dates,
	};
}

export const weekdaysFromMask = (mask: number) => {
	const labels = ["월", "화", "수", "목", "금", "토", "일"];
	return labels.filter((_, index) => (mask & (1 << index)) !== 0);
};

export function timeBucket(hhmm: string) {
	const hour = Number(hhmm.slice(0, 2));
	if (hour >= 5 && hour < 10) return "아침" as const;
	if (hour >= 10 && hour < 15) return "낮" as const;
	if (hour >= 15 && hour < 20) return "저녁" as const;
	return "밤" as const;
}

export type AccessBranch = {
	id: string;
	minutes: number;
	transfers: number;
	reliability: number;
	fareJpy: number | null;
};

export function rankAccess(branches: AccessBranch[]) {
	return [...branches].sort(
		(a, b) =>
			b.reliability - a.reliability ||
			a.transfers - b.transfers ||
			a.minutes - b.minutes ||
			(a.fareJpy ?? Number.MAX_SAFE_INTEGER) -
				(b.fareJpy ?? Number.MAX_SAFE_INTEGER),
	);
}

export * from "./trip";
export * from "./companions";

export * from "./trip-edit";
export * from "./share";
export * from "./settlement";
export * from "./calendar";
export * from "./paste-import";
export * from './expense-import';
